import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LayoutDashboard, ClipboardList, UtensilsCrossed, TrendingUp, Settings, Menu as MenuIcon, X } from 'lucide-react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import Menu from './pages/Menu';
import Earnings from './pages/Earnings';
import Profile from './pages/Profile';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/orders', icon: ClipboardList, label: 'Orders' },
  { path: '/menu', icon: UtensilsCrossed, label: 'Menu' },
  { path: '/earnings', icon: TrendingUp, label: 'Earnings' },
  { path: '/profile', icon: Settings, label: 'Profile' },
];

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { vendor } = useAuth();
  const vendorData = vendor?.vendor || vendor;

  return (
    <>
      {mobileOpen && (
        <div style={styles.overlay} onClick={() => setMobileOpen(false)} />
      )}
      <div style={{ ...styles.sidebar, left: mobileOpen ? '0' : '-260px' }}>
        <div style={styles.sidebarLogo}>
          <div style={styles.logoIconBox}>
            <UtensilsCrossed size={22} color="#fff" />
          </div>
          <div>
            <div style={styles.logoText}>Quickry</div>
            <div style={styles.logoSub}>Vendor Panel</div>
          </div>
          <button style={styles.closeBtn} onClick={() => setMobileOpen(false)}>
            <X size={18} color="#aaa" />
          </button>
        </div>

        <div style={styles.vendorInfo}>
          <div style={styles.vendorAvatar}>
            {(vendorData?.shopName || vendorData?.name || 'V')[0].toUpperCase()}
          </div>
          <div>
            <div style={styles.vendorName}>{vendorData?.shopName || vendorData?.name || 'Vendor'}</div>
            <div style={styles.vendorType}>{vendorData?.businessType || 'Business'}</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {navItems.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                style={{ ...styles.navItem, ...(active ? styles.navActive : {}) }}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
              >
                <Icon size={18} color={active ? '#ff6b35' : '#888'} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const currentNav = navItems.find(n => n.path === location.pathname);

  return (
    <div style={styles.layout}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <div style={styles.main}>
        <div style={styles.topbar}>
          <button style={styles.menuBtn} onClick={() => setMobileOpen(true)}>
            <MenuIcon size={22} color="#1a1a1a" />
          </button>
          <span style={styles.topbarTitle}>{currentNav?.label || 'Quickry Vendor'}</span>
        </div>
        <div style={styles.content}>{children}</div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
  const { isLoggedIn } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={isLoggedIn ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
      <Route path="/menu" element={<ProtectedRoute><Menu /></ProtectedRoute>} />
      <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
);

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#f5f5f5' },
  sidebar: {
    position: 'fixed', top: 0, left: 0, width: '240px', height: '100vh',
    background: '#1a1a2e', zIndex: 100, transition: 'left 0.3s ease',
    overflowY: 'auto', display: 'flex', flexDirection: 'column',
  },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 },
  sidebarLogo: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  logoIconBox: {
    width: '38px', height: '38px', borderRadius: '10px',
    background: '#ff6b35', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  logoText: { color: '#fff', fontWeight: '700', fontSize: '17px', flex: 1 },
  logoSub: { color: '#ff6b35', fontSize: '11px', fontWeight: '500' },
  closeBtn: { background: 'none', marginLeft: 'auto' },
  vendorInfo: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  vendorAvatar: {
    width: '40px', height: '40px', borderRadius: '50%', background: '#ff6b35',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: '700', fontSize: '18px', flexShrink: 0,
  },
  vendorName: { color: '#fff', fontWeight: '600', fontSize: '14px' },
  vendorType: { color: '#888', fontSize: '12px', textTransform: 'capitalize', marginTop: '2px' },
  nav: { padding: '14px 10px', flex: 1 },
  navItem: {
    display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
    padding: '11px 14px', borderRadius: '10px', color: '#888', fontSize: '14px',
    fontWeight: '500', background: 'none', marginBottom: '4px', textAlign: 'left',
    transition: 'all 0.2s',
  },
  navActive: { background: 'rgba(255,107,53,0.12)', color: '#ff6b35' },
  main: { flex: 1, marginLeft: '0', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  topbar: {
    background: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center',
    gap: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', position: 'sticky', top: 0, zIndex: 50,
  },
  menuBtn: { background: 'none', padding: '4px' },
  topbarTitle: { fontWeight: '700', fontSize: '16px', color: '#1a1a1a' },
  content: { flex: 1 },
};

export default App;